import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import type { IssuesCategorySummary, Scope } from '../../../../../shared/issuesV2';
import TeamsModule from '../../../../redux/teams';
import type { TeamSelection } from '../../../../types';

/**
 * Hook which is responsible for fetching the category summary when the component mounts,
 * as well as when the categorySummary changes.
 *
 * categorySummary is set to `null` when the scope changes/is updated, which causes
 * it to be re-fetched with the updated scope. Same for just changing the scope's scanId.
 * This gives the state a chance to update with the correct scope before we fetch.
 *
 * The scope itself is set by useSetIssuesScope in various locations.
 */
const useCategorySummary = (scope: Scope): IssuesCategorySummary | null => {
  const dispatch = useDispatch();
  const categorySummary = useSelector(IssuesV2Store.selectors.getCategorySummary);
  const teamIds = useSelector(TeamsModule.selectors.getTeamSelection);
  const prevTeamIds = useRef<TeamSelection>(teamIds);

  // https://github.com/fossas/FOSSA/blob/73d864ffcfabd6a7280ff13543225a75f9c717be/app/redux/issuesV2/reducer.unit.spec.ts#L640
  //  Whenever categorySummary changes and is null, we re-fetch.
  //  See the actions which update the scope to see where this is set to null.
  useEffect(() => {
    // relying on a referential check between teamIds since the redux store should be immutable
    if (!categorySummary || teamIds !== prevTeamIds.current) {
      prevTeamIds.current = teamIds;
      dispatch(IssuesV2Store.actions.fetchCategorySummary(scope, teamIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We don't want to update when scope is updated (see above).
  }, [categorySummary, dispatch, teamIds]);

  return categorySummary;
};

export default useCategorySummary;
